// Tiny fetch wrapper for the F8M Content Studio API.
// In dev, /api/* is proxied to the Worker on 127.0.0.1:8787 by vite.config.js.
// In prod, the Worker serves the same paths directly.

import { API_BASE } from './constants';

export class ApiError extends Error {
  constructor(message, { status, code, field } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : API_BASE + path;
  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    throw new ApiError(err?.message || 'Network error', { code: 'NETWORK_ERROR' });
  }

  if (res.status === 204) return null;

  let body = null;
  try {
    body = await res.json();
  } catch {
    // Non-JSON response — leave body as null.
  }

  if (!res.ok) {
    throw new ApiError(body?.message || `Request failed: ${res.status}`, {
      status: res.status,
      code: body?.error,
      field: body?.field,
    });
  }
  return body;
}

function buildQuery(params) {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  health: () => apiFetch('/health'),

  events: {
    list: (filters = {}) => apiFetch('/events' + buildQuery(filters)),
    get:    (id)        => apiFetch(`/events/${id}`),
    create: (body)      => apiFetch('/events', { method: 'POST',   body: JSON.stringify(body) }),
    update: (id, body)  => apiFetch(`/events/${id}`, { method: 'PATCH',  body: JSON.stringify(body) }),
    delete: (id)        => apiFetch(`/events/${id}`, { method: 'DELETE' }),
  },

  people: {
    list: (filters = {}) => apiFetch('/people' + buildQuery(filters)),
    get:    (id)        => apiFetch(`/people/${id}`),
    create: (body)      => apiFetch('/people', { method: 'POST',   body: JSON.stringify(body) }),
    update: (id, body)  => apiFetch(`/people/${id}`, { method: 'PATCH',  body: JSON.stringify(body) }),
    delete: (id)        => apiFetch(`/people/${id}`, { method: 'DELETE' }),
  },

  organisations: {
    list: (filters = {}) => apiFetch('/organisations' + buildQuery(filters)),
    get:    (id)        => apiFetch(`/organisations/${id}`),
    create: (body)      => apiFetch('/organisations', { method: 'POST',   body: JSON.stringify(body) }),
    update: (id, body)  => apiFetch(`/organisations/${id}`, { method: 'PATCH',  body: JSON.stringify(body) }),
    delete: (id)        => apiFetch(`/organisations/${id}`, { method: 'DELETE' }),
  },

  brand: {
    getAll: ()           => apiFetch('/brand'),
    get:    (key)        => apiFetch(`/brand/${key}`),
    set:    (key, value) => apiFetch(`/brand/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
    update: (entries)    => apiFetch('/brand', { method: 'PATCH', body: JSON.stringify(entries) }),
  },

  drafts: {
    list:    (filters = {}) => apiFetch('/drafts' + buildQuery(filters)),
    get:     (id)           => apiFetch(`/drafts/${id}`),
    create:  (body)         => apiFetch('/drafts', { method: 'POST', body: JSON.stringify(body) }),
    update:  (id, body)     => apiFetch(`/drafts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete:  (id)           => apiFetch(`/drafts/${id}`, { method: 'DELETE' }),
    history: (id)           => apiFetch(`/drafts/${id}/history`),
  },

  notes: {
    list:    (filters = {}) => apiFetch('/notes' + buildQuery(filters)),
    create:  (body)         => apiFetch('/notes', { method: 'POST', body: JSON.stringify(body) }),
    update:  (id, body)     => apiFetch(`/notes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete:  (id)           => apiFetch(`/notes/${id}`, { method: 'DELETE' }),
  },

  notifications: {
    list:         (params = {}) => apiFetch('/notifications' + buildQuery(params)),
    unreadCount:  ()            => apiFetch('/notifications/unread-count'),
    markRead:     (id)          => apiFetch(`/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead:  ()            => apiFetch('/notifications/mark-all-read', { method: 'POST' }),
  },

  speakers: {
    list:   (eventId)            => apiFetch(`/events/${eventId}/speakers`),
    create: (eventId, body)      => apiFetch(`/events/${eventId}/speakers`, { method: 'POST',   body: JSON.stringify(body) }),
    update: (eventId, id, body)  => apiFetch(`/events/${eventId}/speakers/${id}`, { method: 'PATCH',  body: JSON.stringify(body) }),
    delete: (eventId, id)        => apiFetch(`/events/${eventId}/speakers/${id}`, { method: 'DELETE' }),
  },

  sessions: {
    list:   (eventId)            => apiFetch(`/events/${eventId}/sessions`),
    create: (eventId, body)      => apiFetch(`/events/${eventId}/sessions`, { method: 'POST',   body: JSON.stringify(body) }),
    update: (eventId, id, body)  => apiFetch(`/events/${eventId}/sessions/${id}`, { method: 'PATCH',  body: JSON.stringify(body) }),
    delete: (eventId, id)        => apiFetch(`/events/${eventId}/sessions/${id}`, { method: 'DELETE' }),
  },

  calendarDates: {
    list: (filters = {}) => apiFetch('/calendar-dates' + buildQuery(filters)),
  },
};
